import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import IntersectionObserver from '../IntersectionObserver/IntersectionObserver';

import * as Styled from './StickyBar.styled';

/**
 * A generic sticky bar which can optionally style the container when it becomes sticky
 */
const StickyBar = ({
  children,
  topOffset,
  withStickyStyle,
  enabled,
  onIntersect,
  ...rest
}) => {
  const targetRef = useRef(null);

  return (
    <IntersectionObserver onIntersect={onIntersect} target={targetRef}>
      {({ isIntersecting }) => (
        <>
          {withStickyStyle && (
            <Styled.StickyBarIntersection
              topOffset={topOffset}
              ref={targetRef}
              data-sticky={!isIntersecting}
            />
          )}
          <Styled.StickyBar
            topOffset={topOffset}
            isSticky={!isIntersecting && withStickyStyle}
            enabled={enabled}
            data-testid="stickybar"
            {...rest}
          >
            {children}
          </Styled.StickyBar>
        </>
      )}
    </IntersectionObserver>
  );
};

StickyBar.propTypes = {
  /**
   * Nodes to render in StickyBar tree
   */
  children: PropTypes.node.isRequired,
  /**
   * The offset from the top of the container in pixels
   */
  topOffset: PropTypes.string,
  /**
   * Include detection to determine when element is sticky and corresponding style change
   */
  withStickyStyle: PropTypes.bool,
  /**
   * Enable stickyness based on breakpoint
   */
  enabled: PropTypes.shape({
    small: PropTypes.bool,
    medium: PropTypes.bool,
    large: PropTypes.bool
  }),
  /**
   * Callback function to run when sticky state changes
   */
  onIntersect: PropTypes.func
};

StickyBar.defaultProps = {
  topOffset: '0px',
  withStickyStyle: true,
  enabled: {
    small: true,
    medium: true,
    large: true
  },
  onIntersect: null
};

export default StickyBar;
